import { LineaVenta } from "../models/LineaVentaModel";
import { UserPayload } from "../models/SiteModel";
import { apiFetch } from "./fetchService";
import { parseDecimalValue } from "../utils/NumberParser";
import { LocalModel } from "../models/LocalModel";

const URLAPI = process.env.REACT_APP_BASE_URL;

const unwrapApiData = (response: any) => response?.data ?? response?.Data ?? response;

const normalizeObject = (value: any) => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }

  return value ?? {};
};

const normalizeDetalle = (detalle: any): MesaDetalleResponse[] => {
  if (typeof detalle === "string") {
    try {
      return JSON.parse(detalle);
    } catch {
      return [];
    }
  }

  return Array.isArray(detalle) ? detalle : [];
};

export interface MesaAbiertaResponse {
  abierta: boolean;
  comanda?: MesaComandaResponse;
  detalle: MesaDetalleResponse[];
}

export interface MesaComandaResponse {
  idMesa: number;
  idMozo: number;
  idComanda: number;
  idLocal: number;
  idEmpleado: number;
  pv: number;
  empleado: string;
  fecha: string;
  total: number;
  idCaja: number;
  nombre: string;
}

export interface MesaDetalleResponse {
  idComanda: number;
  idLocal: number;
  pv: number;
  idProducto: number;
  producto: string;
  cantidad: number;
  unitario: number;
  enMesa: boolean;
  seProduce: boolean;
  esPadre: boolean;
  nota: string;
}

export interface MapaMesaResponse {
  idMesaMapa: number;
  numero: number;
  nombre: string;
  x: number;
  y: number;
  ancho: number;
  alto: number;
  forma: "Rectangular" | "Redonda";
  activo: boolean;
}

export interface MapaMesasSectorResponse {
  idSector: number;
  nombre: string;
  orden: number;
  activo: boolean;
  mesas: MapaMesaResponse[];
}

export interface MapaMesasResponse {
  idLocal: number;
  sectores: MapaMesasSectorResponse[];
}

export async function fetchGetLocalesComandaMesa(user: UserPayload, signal?: AbortSignal): Promise<LocalModel[]> {
  const response = await apiFetch(user.token, `${URLAPI}/Mesa/LocalesDisponibles`, { signal });
  const raw = unwrapApiData(response);
  return Array.isArray(raw) ? raw.map((local: any) => ({
    idLocal: String(local.idLocal ?? local.IdLocal ?? ""),
    razonSocial: String(local.razonSocial ?? local.RazonSocial ?? ""),
    fantasia: String(local.fantasia ?? local.Fantasia ?? ""),
    pvComandaMesaWeb: Number(local.pvComandaMesaWeb ?? local.PvComandaMesaWeb ?? 0)
  })) : [];
}

export async function fetchGetMapaMesas(user: UserPayload, idLocal: number, signal?: AbortSignal): Promise<MapaMesasResponse> {
  const response = await apiFetch(user.token, `${URLAPI}/Mesa/Mapa/${idLocal}`, { signal });
  const raw = normalizeObject(unwrapApiData(response));
  const sectoresRaw = raw?.sectores ?? raw?.Sectores ?? [];
  return {
    idLocal: Number(raw?.idLocal ?? raw?.IdLocal ?? idLocal),
    sectores: (Array.isArray(sectoresRaw) ? sectoresRaw : []).map((sector: any) => {
      const mesasRaw = sector?.mesas ?? sector?.Mesas ?? [];
      return {
        idSector: Number(sector?.idSector ?? sector?.IdSector ?? 0),
        nombre: String(sector?.nombre ?? sector?.Nombre ?? ""),
        orden: Number(sector?.orden ?? sector?.Orden ?? 0),
        activo: Boolean(sector?.activo ?? sector?.Activo ?? true),
        mesas: (Array.isArray(mesasRaw) ? mesasRaw : []).map((mesa: any): MapaMesaResponse => ({
          idMesaMapa: Number(mesa?.idMesaMapa ?? mesa?.IdMesaMapa ?? 0),
          numero: Number(mesa?.numero ?? mesa?.Numero ?? 0),
          nombre: String(mesa?.nombre ?? mesa?.Nombre ?? ""),
          x: Number(mesa?.x ?? mesa?.X ?? 0), y: Number(mesa?.y ?? mesa?.Y ?? 0),
          ancho: Number(mesa?.ancho ?? mesa?.Ancho ?? 120), alto: Number(mesa?.alto ?? mesa?.Alto ?? 80),
          forma: String(mesa?.forma ?? mesa?.Forma ?? "Rectangular").toLowerCase() === "redonda" ? "Redonda" : "Rectangular",
          activo: Boolean(mesa?.activo ?? mesa?.Activo ?? true)
        }))
      };
    }).sort((a: MapaMesasSectorResponse, b: MapaMesasSectorResponse) => a.orden - b.orden)
  };
}

export interface MesaGuardarOptions {
  pv?: number;
  idCaja?: number;
  idEmpleado?: number;
  eliminarAnterior?: boolean;
  idComandasOriginales?: number[];
  nombre?: string;
  idMesa?: number;
}

const getUnitario = (linea: LineaVenta): number => {
  const producto = linea.producto as any;
  return parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? producto.precio ?? producto.Precio ?? 0);
};

const mapLineaMesaToPayload = (linea: LineaVenta) => ({
  idProducto: Number((linea.producto as any).IDPRODUCTO ?? (linea.producto as any).idProducto ?? (linea.producto as any).IdProducto ?? 0),
  producto: String((linea.producto as any).PRODUCTO ?? (linea.producto as any).producto ?? (linea.producto as any).Producto ?? ""),
  cantidad: linea.cantidad,
  unitario: getUnitario(linea),
  enMesa: true,
  nota: linea.notas || ""
});

const agruparLineasMesa = (lineas: LineaVenta[]): LineaVenta[] => {
  const grupos = new Map<string, LineaVenta>();

  for (const linea of lineas) {
    const producto = linea.producto as any;
    const idProducto = Number(producto.IDPRODUCTO ?? producto.idProducto ?? producto.IdProducto ?? 0);
    const unitario = getUnitario(linea);
    const nota = linea.notas || "";
    const idComanda = linea.idComanda ?? 0;
    const key = `${idComanda}|${idProducto}|${unitario}|${nota}`;

    const existente = grupos.get(key);
    if (existente) {
      existente.cantidad += linea.cantidad;
    } else {
      grupos.set(key, {
        ...linea,
        producto: {
          ...linea.producto,
          IDPRODUCTO: idProducto,
          PRODUCTO: String(producto.PRODUCTO ?? producto.producto ?? producto.Producto ?? "")
        },
        cantidad: linea.cantidad,
        notas: nota
      });
    }
  }

  return Array.from(grupos.values());
};

export async function fetchGetMesaAbierta(
  user: UserPayload,
  idLocal: number,
  pv: number,
  numeroMesa: string,
  signal?: AbortSignal
): Promise<MesaAbiertaResponse> {
  const response = await apiFetch(
    user.token,
    `${URLAPI}/Mesa/ObtenerAbierta/${idLocal}/${pv}/${encodeURIComponent(numeroMesa)}`,
    { signal }
  );
  const mesa = normalizeObject(unwrapApiData(response));

  return {
    abierta: Boolean(mesa?.abierta ?? mesa?.Abierta),
    comanda: mesa?.comanda ?? mesa?.Comanda,
    detalle: normalizeDetalle(mesa?.detalle ?? mesa?.Detalle)
  };
}

export async function fetchGetMesasAbiertas(
  user: UserPayload,
  idLocal: number,
  pv: number,
  signal?: AbortSignal
): Promise<MesaComandaResponse[]> {
  const response = await apiFetch(
    user.token,
    `${URLAPI}/Mesa/ObtenerAbiertas/${idLocal}/${pv}`,
    { signal }
  );
  const data = unwrapApiData(response);
  const comandas = typeof data === "string" ? JSON.parse(data) : data;
  if (!Array.isArray(comandas)) return [];

  return comandas.map((item: any) => ({
    idMesa: Number(item.idMesa ?? item.IdMesa ?? 0),
    idMozo: Number(item.idMozo ?? item.IdMozo ?? 0),
    idComanda: Number(item.idComanda ?? item.IdComanda ?? 0),
    idLocal: Number(item.idLocal ?? item.IdLocal ?? 0),
    idEmpleado: Number(item.idEmpleado ?? item.IdEmpleado ?? 0),
    pv: Number(item.pv ?? item.Pv ?? 0),
    empleado: String(item.empleado ?? item.Empleado ?? ""),
    fecha: String(item.fecha ?? item.Fecha ?? ""),
    total: parseDecimalValue(item.total ?? item.Total ?? 0),
    idCaja: Number(item.idCaja ?? item.IdCaja ?? 0),
    nombre: String(item.nombre ?? item.Nombre ?? "")
  }));
}

export async function fetchGetMesaAbiertaPorNombre(
  user: UserPayload,
  idLocal: number,
  pv: number,
  nombre: string,
  signal?: AbortSignal
): Promise<MesaAbiertaResponse> {
  const response = await apiFetch(
    user.token,
    `${URLAPI}/Mesa/ObtenerAbiertaPorNombre/${idLocal}/${pv}?nombre=${encodeURIComponent(nombre)}`,
    { signal }
  );
  const mesa = normalizeObject(unwrapApiData(response));
  return {
    abierta: Boolean(mesa?.abierta ?? mesa?.Abierta),
    comanda: mesa?.comanda ?? mesa?.Comanda,
    detalle: normalizeDetalle(mesa?.detalle ?? mesa?.Detalle)
  };
}

export async function fetchGuardarComandaMesa(
  user: UserPayload,
  idLocal: number,
  numeroMesa: string,
  lineas: LineaVenta[],
  options: MesaGuardarOptions = {}
) {
  const pv = Number(options.pv ?? 0);
  if (!Number.isInteger(pv) || pv <= 0) {
    throw new Error("El local seleccionado no tiene un punto de venta asociado válido para Comanda Mesa Web.");
  }
  const idMesa = options.idMesa ?? Number(numeroMesa);
  const idComandaNombrada = idMesa < 0
    ? (options.idComandasOriginales ?? []).find((id) => id > 0)
    : undefined;
  const lineasParaGuardar = idComandaNombrada
    ? lineas.map((linea) => (linea.idComanda ? linea : { ...linea, idComanda: idComandaNombrada }))
    : lineas;
  const lineasAgrupadas = agruparLineasMesa(lineasParaGuardar);
  const lineasExistentes = lineasAgrupadas.filter((linea) => Boolean(linea.idComanda));
  const lineasNuevas = lineasAgrupadas.filter((linea) => !linea.idComanda);

  const gruposExistentes = lineasExistentes.reduce((acc: Record<number, LineaVenta[]>, linea) => {
    const idComanda = linea.idComanda ?? 0;
    if (!acc[idComanda]) {
      acc[idComanda] = [];
    }
    acc[idComanda].push(linea);
    return acc;
  }, {});

  const idsActuales = Object.keys(gruposExistentes).map(Number);
  const idsAnular = (options.idComandasOriginales ?? []).filter((idComanda) => !idsActuales.includes(idComanda));
  const nuevaComanda = lineasNuevas.length > 0 ? {
        idMesa: Number.isFinite(idMesa) ? idMesa : 0,
        nombre: options.nombre?.trim() ?? "",
        idLocal,
        idEmpleado: options.idEmpleado ?? 0,
        idCaja: options.idCaja ?? 0,
        total: lineasNuevas.reduce((total, linea) => total + getUnitario(linea) * linea.cantidad, 0),
        detalle: lineasNuevas.map(mapLineaMesaToPayload)
      } : null;
  return apiFetch(user.token, `${URLAPI}/Mesa/GuardarCompleta`, {
    method: "POST",
    body: JSON.stringify({
      idLocal,
      nuevaComanda,
      ediciones: Object.keys(gruposExistentes).map((id) => ({
        idComanda: Number(id),
        detalle: gruposExistentes[Number(id)].map(mapLineaMesaToPayload)
      })),
      idComandasAnular: idsAnular
    })
  });
}

export function mapMesaDetalleToLineaVenta(linea: MesaDetalleResponse): LineaVenta {
  const source = linea as any;
  return {
    producto: {
      IDPRODUCTO: Number(source.idProducto ?? source.IdProducto ?? source.IDPRODUCTO ?? 0),
      CODIGO: String(source.codigo ?? source.Codigo ?? source.CODIGO ?? ""),
      PRODUCTO: String(source.producto ?? source.Producto ?? source.PRODUCTO ?? ""),
      CONTADO: parseDecimalValue(source.unitario ?? source.Unitario ?? source.UNITARIO ?? 0)
    },
    cantidad: parseDecimalValue(source.cantidad ?? source.Cantidad ?? source.CANTIDAD ?? 0),
    idComanda: Number(source.idComanda ?? source.IdComanda ?? source.IDCOMANDA ?? 0),
    notas: String(source.nota ?? source.Nota ?? source.NOTA ?? "")
  };
}
